import type {FrqRubricCriterion} from 'lib/rubric/types';

import type {ManualEvaluation} from './FrqEvaluationSummary.types';

// The server stores totals as floats, so a total that matches the rubric can differ in the last bits.
const SCORE_TOLERANCE = 1e-6;

/**
 * Points earned on criteria the rubric still defines, and how many of them were scored. A score
 * left behind by a deleted criterion is ignored, matching how the server derives a total on publish.
 */
export function sumDefinedCriteria(
  evaluation: ManualEvaluation,
  criteria: readonly FrqRubricCriterion[]
): {earned: number; scoredCount: number} {
  const defined = new Set(criteria.map((c) => c.id));
  return evaluation.criteriaScores.reduce(
    (acc, c) =>
      typeof c.score === 'number' && defined.has(c.criterionId)
        ? {earned: acc.earned + c.score, scoredCount: acc.scoredCount + 1}
        : acc,
    {earned: 0, scoredCount: 0}
  );
}

/**
 * Whether the total a teacher set disagrees with the per-criterion scores shown beneath it, so the
 * released grade has to say the total was adjusted. With no criterion scored there is nothing for
 * the total to disagree with.
 */
export function isTotalAdjusted(
  evaluation: ManualEvaluation,
  criteria: readonly FrqRubricCriterion[]
): boolean {
  if (typeof evaluation.overallScore !== 'number') {
    return false;
  }
  const {earned, scoredCount} = sumDefinedCriteria(evaluation, criteria);
  return scoredCount > 0 && Math.abs(evaluation.overallScore - earned) > SCORE_TOLERANCE;
}
